import React, { FC } from 'react';

import { ProjectOutlined, TableOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Layout, Result, Row, Segmented, theme, Tooltip } from 'antd';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { Module } from '@app/models/users';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { OrdersViewType } from '@app/store/currentSessionReducer/interfaces';
import { HeaderTitle } from '@components/HeaderTitle';
import { Paper } from '@components/Paper';

import { ExportButton } from '../../components/ExportButton';
import { OrdersBoard } from '../../components/OrdersBoard';
import { OrdersEmpty } from '../../components/OrdersEmpty';
import { OrdersFilters } from '../../components/OrdersFilters';
import { OrdersTable } from '../../components/OrdersTable';
import { toOrdersFilters } from '../../data/filters';
import {
  hasOrdersEndpoint,
  useCounterpartyOrdersCountsQuery,
  useCounterpartyOrdersQuery,
  useOrdersSearchParams,
  useOrdersTabs,
} from '../../hooks';
import { ALL_TAB, IOrdersFilters, OrdersTab } from '../../interfaces';

const BOARD_LIMIT = 100;
const NO_FILTERS = toOrdersFilters({});

export const List: FC = () => {
  const { token } = theme.useToken();
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const { tab: requestedTab, view, update } = useOrdersSearchParams();
  const { current, pageSize, setPagination } = useTableParams();
  const { tabs } = useOrdersTabs();
  // У каждой вкладки свои фильтры: статусы во вкладках разные
  const [filtersByTab, setFiltersByTab] = React.useState<Partial<Record<OrdersTab, IOrdersFilters>>>({});

  const tab = tabs.includes(requestedTab) ? requestedTab : ALL_TAB;
  const isBoard = view === OrdersViewType.BOARD && tab === Module.Warehouse;
  const filters = filtersByTab[tab] || NO_FILTERS;
  const isFiltered = !isEqual(filters, NO_FILTERS);

  const handleFiltersChange = (nextFilters: IOrdersFilters, restored: boolean) => {
    if (isEqual(nextFilters, filters)) {
      return;
    }
    setFiltersByTab((value) => ({ ...value, [tab]: nextFilters }));
    // Сохранённые фильтры вернулись при открытии страницы - её номер остаётся прежним
    if (!restored) {
      update({});
    }
  };

  const list = useCounterpartyOrdersQuery(tab, {
    ...filters,
    limit: isBoard ? BOARD_LIMIT : pageSize,
    offset: isBoard ? 0 : (current - 1) * pageSize,
  });
  const counts = useCounterpartyOrdersCountsQuery();

  const countOf = (target: OrdersTab): number | undefined => {
    if (target === ALL_TAB) {
      return counts.data?.all;
    }
    if (target === Module.Warehouse) {
      return counts.data?.warehouse;
    }
    return target === Module.Delivery ? counts.data?.delivery : undefined;
  };

  const rows = list.data?.items || [];
  const empty = isFiltered ? (
    <OrdersEmpty title={ordersT.t('empty.filteredTitle')} text={ordersT.t('empty.filteredText')} />
  ) : (
    <OrdersEmpty title={ordersT.t('empty.title')} text={ordersT.t('empty.text')} />
  );

  const renderContent = () => {
    if (!hasOrdersEndpoint(tab)) {
      return <OrdersEmpty title={ordersT.t('empty.supplyTitle')} text={ordersT.t('empty.supplyText')} />;
    }

    const renderOrders = () => {
      if (list.isError) {
        return (
          <Result
            status="error"
            subTitle={getErrorMessage(list.error)}
            extra={<Button onClick={() => list.refetch()}>{commonT.t('buttonReload')}</Button>}
          />
        );
      }
      if (isBoard) {
        return <OrdersBoard rows={rows} loading={list.isFetching} total={list.data?.total} empty={empty} />;
      }
      return (
        <OrdersTable
          tab={tab}
          rows={rows}
          total={list.data?.total}
          loading={list.isFetching}
          current={current}
          pageSize={pageSize}
          onPaginationChange={setPagination}
          empty={empty}
        />
      );
    };

    return (
      <Flex vertical gap={16}>
        {/* Фильтры остаются и при ошибке списка: сохранённое условие иначе нельзя было бы снять */}
        <OrdersFilters
          key={tab}
          tab={tab}
          total={list.isError ? undefined : list.data?.total}
          onChange={handleFiltersChange}
        />
        {!list.isError && rows.some((row) => row.cargo_unavailable) && (
          <Alert type="warning" showIcon title={ordersT.t('cargoUnavailableAlert')} />
        )}
        {renderOrders()}
      </Flex>
    );
  };

  return (
    <Layout>
      <Layout.Header>
        <Row justify="space-between" align="middle" wrap={false}>
          <HeaderTitle title={ordersT.t('title')}>
            <Segmented<OrdersTab>
              value={tab}
              onChange={(value) => update({ tab: value === ALL_TAB ? undefined : value })}
              options={tabs.map((target) => {
                const count = hasOrdersEndpoint(target) ? countOf(target) : undefined;
                return {
                  value: target,
                  label: (
                    <Flex align="center" gap={8}>
                      {ordersT.t(`tabs.${target}` as const)}
                      {count !== undefined && (
                        <span
                          style={{
                            minWidth: 20,
                            padding: '0 6px',
                            borderRadius: 10,
                            background: token.colorPrimaryBg,
                            color: token.colorPrimary,
                            fontSize: 12,
                            lineHeight: '18px',
                          }}
                        >
                          {count}
                        </span>
                      )}
                    </Flex>
                  ),
                };
              })}
            />
          </HeaderTitle>
          <Flex align="center" gap={16}>
            <Segmented<OrdersViewType>
              value={isBoard ? OrdersViewType.BOARD : OrdersViewType.LIST}
              onChange={(value) => update({ view: value === OrdersViewType.BOARD ? value : undefined })}
              options={[
                {
                  value: OrdersViewType.LIST,
                  label: (
                    <Tooltip title={ordersT.t('view.table')}>
                      <TableOutlined aria-label={ordersT.t('view.table')} />
                    </Tooltip>
                  ),
                },
                {
                  value: OrdersViewType.BOARD,
                  disabled: tab !== Module.Warehouse,
                  label: (
                    <Tooltip
                      title={tab === Module.Warehouse ? ordersT.t('view.board') : ordersT.t('view.boardInWarehouseTab')}
                    >
                      <ProjectOutlined aria-label={ordersT.t('view.board')} />
                    </Tooltip>
                  ),
                },
              ]}
            />
            <ExportButton tab={tab} filters={filters} />
          </Flex>
        </Row>
      </Layout.Header>

      <Paper style={{ margin: 24, padding: 24 }}>{renderContent()}</Paper>
    </Layout>
  );
};
